/**
 * Analytics Service
 * 
 * This service handles tracking events for the landing page funnel and user actions.
 * It provides a unified interface for multiple analytics platforms.
 */

// Event names for tracking
export const ANALYTICS_EVENTS = {
  // Landing page events
  LANDING_PAGE_VIEW: 'landing_page_view',
  LANDING_PASTE_CLICKED: 'landing_paste_clicked',
  LANDING_URL_ENTERED: 'landing_url_entered',
  LANDING_CTA_CLICKED: 'landing_cta_clicked',
  
  // Auth flow events
  SIGNUP_STARTED: 'signup_started',
  SIGNUP_COMPLETED: 'signup_completed',
  SIGNUP_COMPLETED_WITH_PENDING_URL: 'signup_completed_with_pending_url',
  LOGIN_COMPLETED: 'login_completed',
  LOGIN_COMPLETED_WITH_PENDING_URL: 'login_completed_with_pending_url',
  
  // Job events
  JOB_STARTED_FROM_LANDING: 'job_started_from_landing',
  JOB_STARTED: 'job_started',
  JOB_COMPLETED: 'job_completed',
  JOB_FAILED: 'job_failed',
  
  // Payment events
  PAYMENT_CLICKED: 'payment_clicked',
  PAYMENT_SUCCESS: 'payment_success',
  PAYMENT_FAILED: 'payment_failed',
  
  // Engagement events
  RESUME_PENDING_URL: 'resume_pending_url',
  PENDING_URL_PREFILLED: 'pending_url_prefilled',
};

/**
 * Check if Google Analytics is available
 */
const hasGtag = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Check if Facebook Pixel is available
 */
const hasFbq = () => {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
};

/**
 * Track an event with Google Analytics
 */
const trackGtag = (eventName, params = {}) => {
  if (hasGtag()) {
    window.gtag('event', eventName, params);
  }
};

/**
 * Track an event with Facebook Pixel
 */
const trackFbq = (eventName, params = {}) => {
  if (hasFbq()) {
    // Map our events to Facebook standard events where applicable
    const fbEventMap = {
      [ANALYTICS_EVENTS.SIGNUP_COMPLETED]: 'CompleteRegistration',
      [ANALYTICS_EVENTS.PAYMENT_SUCCESS]: 'Purchase',
      [ANALYTICS_EVENTS.LANDING_CTA_CLICKED]: 'Lead',
    };
    
    const fbEvent = fbEventMap[eventName];
    if (fbEvent) {
      window.fbq('track', fbEvent, params);
    } else {
      window.fbq('trackCustom', eventName, params);
    }
  }
};

/**
 * Log event in development mode
 */
const logDev = (eventName, params = {}) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Analytics] ${eventName}`, params);
  }
};

export const analyticsService = {
  /**
   * Track a generic event
   * @param {string} eventName - The event name
   * @param {Object} params - Event parameters
   */
  track(eventName, params = {}) {
    const enrichedParams = {
      ...params,
      timestamp: new Date().toISOString(),
      page_url: typeof window !== 'undefined' ? window.location.href : null,
    };

    logDev(eventName, enrichedParams);
    trackGtag(eventName, enrichedParams);
    trackFbq(eventName, enrichedParams);
  },

  /**
   * Track landing page view
   * @param {Object} params - Additional parameters
   */
  trackLandingPageView(params = {}) {
    this.track(ANALYTICS_EVENTS.LANDING_PAGE_VIEW, {
      source: params.source || 'direct',
      utm_source: params.utm_source,
      utm_medium: params.utm_medium,
      utm_campaign: params.utm_campaign,
    });
  },

  /**
   * Track when user pastes/enters a URL on landing
   * @param {string} url - The URL entered (will be anonymized)
   */
  trackLandingUrlEntered(url) {
    this.track(ANALYTICS_EVENTS.LANDING_URL_ENTERED, {
      has_valid_url: !!url,
      url_length: url?.length || 0,
    });
  },

  /**
   * Track CTA click on landing page
   * @param {Object} params - Click parameters
   */
  trackLandingCtaClicked(params = {}) {
    this.track(ANALYTICS_EVENTS.LANDING_CTA_CLICKED, {
      cta_location: params.location || 'hero',
      has_url: params.hasUrl || false,
    });
  },

  /**
   * Track signup completion
   * @param {Object} params - Signup parameters
   */
  trackSignupCompleted(params = {}) {
    const eventName = params.hasPendingUrl
      ? ANALYTICS_EVENTS.SIGNUP_COMPLETED_WITH_PENDING_URL
      : ANALYTICS_EVENTS.SIGNUP_COMPLETED;

    this.track(eventName, {
      method: params.method || 'email', // 'email' or 'google'
      has_pending_url: params.hasPendingUrl || false,
    });
  },

  /**
   * Track login completion
   * @param {Object} params - Login parameters
   */
  trackLoginCompleted(params = {}) {
    const eventName = params.hasPendingUrl
      ? ANALYTICS_EVENTS.LOGIN_COMPLETED_WITH_PENDING_URL
      : ANALYTICS_EVENTS.LOGIN_COMPLETED;

    this.track(eventName, {
      method: params.method || 'email',
      has_pending_url: params.hasPendingUrl || false,
    });
  },

  /**
   * Track job started
   * @param {Object} params - Job parameters
   */
  trackJobStarted(params = {}) {
    const eventName = params.fromLanding
      ? ANALYTICS_EVENTS.JOB_STARTED_FROM_LANDING
      : ANALYTICS_EVENTS.JOB_STARTED;

    this.track(eventName, {
      job_type: params.jobType || 'upscale',
      from_landing: params.fromLanding || false,
      credits_used: params.creditsUsed || 0,
    });
  },

  /**
   * Track job completed
   * @param {Object} params - Job parameters
   */
  trackJobCompleted(params = {}) {
    this.track(ANALYTICS_EVENTS.JOB_COMPLETED, {
      job_type: params.jobType || 'upscale',
      duration_seconds: params.durationSeconds,
    });
  },

  /**
   * Track payment click
   * @param {Object} params - Payment parameters
   */
  trackPaymentClicked(params = {}) {
    this.track(ANALYTICS_EVENTS.PAYMENT_CLICKED, {
      package_id: params.packageId,
      amount: params.amount,
      currency: params.currency || 'EUR',
    });
  },

  /**
   * Track payment success
   * @param {Object} params - Payment parameters
   */
  trackPaymentSuccess(params = {}) {
    this.track(ANALYTICS_EVENTS.PAYMENT_SUCCESS, {
      package_id: params.packageId,
      amount: params.amount,
      currency: params.currency || 'EUR',
      transaction_id: params.transactionId,
    });
  },

  /**
   * Track pending URL prefill
   */
  trackPendingUrlPrefilled() {
    this.track(ANALYTICS_EVENTS.PENDING_URL_PREFILLED);
  },

  /**
   * Track resume pending URL prompt
   * @param {boolean} accepted - Whether user accepted
   */
  trackResumePendingUrl(accepted) {
    this.track(ANALYTICS_EVENTS.RESUME_PENDING_URL, {
      accepted,
    });
  },

  /**
   * Set user properties for analytics
   * @param {Object} userProps - User properties
   */
  setUserProperties(userProps = {}) {
    if (hasGtag()) {
      window.gtag('set', 'user_properties', userProps);
    }
  },

  /**
   * Set user ID for analytics
   * @param {string} userId - The user ID
   */
  setUserId(userId) {
    if (hasGtag()) {
      window.gtag('config', process.env.REACT_APP_GA_ID, {
        user_id: userId,
      });
    }
  },
};

export default analyticsService;
